import { useCallback, useEffect, useState } from 'react'
import { listSecrets, createSecret, updateSecret, deleteSecret } from '@/api/secrets'
import type { SecretMetadata, SecretScope } from '@/types'
import SecretTable from '@/components/SecretTable'
import SecretForm from '@/components/SecretForm'

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'replace'; secret: SecretMetadata }
  | { type: 'delete'; secret: SecretMetadata }

const SCOPE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All scopes' },
  { value: 'global', label: 'Global' },
  { value: 'site', label: 'Site' },
  { value: 'node', label: 'Node' },
]

export default function SecretsPage() {
  return <SecretsPageInner />
}

function SecretsPageInner() {
  const [secrets, setSecrets] = useState<SecretMetadata[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [scopeFilter, setScopeFilter] = useState('')
  const [modal, setModal] = useState<ModalState>({ type: 'none' })
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const fetchSecrets = useCallback(async () => {
    try {
      const params: { scope?: string } = {}
      if (scopeFilter) params.scope = scopeFilter
      const res = await listSecrets(params)
      setSecrets(res.items)
      setError(null)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Failed to load secrets.')
    } finally {
      setLoading(false)
    }
  }, [scopeFilter])

  useEffect(() => {
    setLoading(true)
    fetchSecrets()
  }, [fetchSecrets])

  function openCreate() {
    setFormError(null)
    setModal({ type: 'create' })
  }

  function openReplace(secret: SecretMetadata) {
    setFormError(null)
    setModal({ type: 'replace', secret })
  }

  function openDelete(secret: SecretMetadata) {
    setModal({ type: 'delete', secret })
  }

  function closeModal() {
    setModal({ type: 'none' })
    setFormError(null)
  }

  async function handleCreate(data: {
    name: string
    scope: SecretScope
    scope_key: string
    plaintext_value: string
  }) {
    setSubmitting(true)
    setFormError(null)
    try {
      await createSecret({
        name: data.name,
        scope: data.scope,
        scope_key: data.scope_key,
        plaintext_value: data.plaintext_value,
      })
      closeModal()
      showToast('Secret created successfully.')
      await fetchSecrets()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setFormError(err?.message ?? 'Failed to create secret.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleReplace(data: {
    name: string
    scope: SecretScope
    scope_key: string
    plaintext_value: string
  }) {
    if (modal.type !== 'replace') return
    setSubmitting(true)
    setFormError(null)
    try {
      const updated = await updateSecret(modal.secret.id, {
        plaintext_value: data.plaintext_value,
      })
      closeModal()
      showToast(`Secret "${updated.name}" updated to version ${updated.version}.`)
      await fetchSecrets()
    } catch (e: unknown) {
      const err = e as { message?: string }
      setFormError(err?.message ?? 'Failed to replace secret.')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDeleteConfirm() {
    if (modal.type !== 'delete') return
    setDeleting(true)
    try {
      await deleteSecret(modal.secret.id)
      closeModal()
      showToast(`Secret "${modal.secret.name}" deleted.`)
      await fetchSecrets()
    } catch (e: unknown) {
      const err = e as { message?: string }
      showToast(`Delete failed: ${err?.message ?? 'unknown error'}`)
      closeModal()
    } finally {
      setDeleting(false)
    }
  }

  const filteredSecrets = scopeFilter
    ? secrets.filter((s) => s.scope === scopeFilter)
    : secrets

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Secrets</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#888' }}>
            {filteredSecrets.length} secret{filteredSecrets.length !== 1 ? 's' : ''}
          </span>
          <select
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
            style={selectStyle}
          >
            {SCOPE_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button onClick={openCreate} style={primaryBtnStyle}>
            + New Secret
          </button>
        </div>
      </div>

      {/* Security notice */}
      <div style={noticeStyle}>
        Secret values are encrypted at rest and never displayed. Only the metadata is shown below.
        The plaintext value is visible only during creation/replacement and is never stored in the browser.
      </div>

      {error && !loading && (
        <div style={errBannerStyle}>
          {error}
          <button onClick={fetchSecrets} style={{ marginLeft: 12, fontSize: 12, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      <div style={cardStyle}>
        <SecretTable
          secrets={filteredSecrets}
          onReplace={openReplace}
          onDelete={openDelete}
          loading={loading}
        />
      </div>

      {/* Create modal */}
      {modal.type === 'create' && (
        <Modal title="New Secret" onClose={closeModal}>
          <SecretForm
            mode="create"
            onSubmit={handleCreate}
            onCancel={closeModal}
            submitting={submitting}
            error={formError}
          />
        </Modal>
      )}

      {/* Replace modal */}
      {modal.type === 'replace' && (
        <Modal title={`Replace "${modal.secret.name}"`} onClose={closeModal}>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 14, marginTop: 0 }}>
            Current version: v{modal.secret.version}. After replacing, version will increment by 1.
          </p>
          <SecretForm
            mode="update"
            secret={modal.secret}
            onSubmit={handleReplace}
            onCancel={closeModal}
            submitting={submitting}
            error={formError}
          />
        </Modal>
      )}

      {/* Delete confirm */}
      {modal.type === 'delete' && (
        <Modal title="Delete Secret" onClose={closeModal}>
          <div style={warningBoxStyle}>
            <strong>Warning:</strong> Any config profile referencing{' '}
            <code style={{ fontFamily: 'monospace' }}>secret:{modal.secret.name}</code> or{' '}
            <code style={{ fontFamily: 'monospace' }}>secret:{modal.secret.id}</code> will break
            after deletion. Verify no active config references this secret before proceeding.
          </div>
          <p style={{ fontSize: 14, color: '#444', marginBottom: 20 }}>
            Delete secret <strong>{modal.secret.name}</strong> (scope: {modal.secret.scope} /{' '}
            {modal.secret.scope_key})?
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={closeModal} style={secondaryBtnStyle} disabled={deleting}>
              Cancel
            </button>
            <button onClick={handleDeleteConfirm} style={dangerBtnStyle} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete secret'}
            </button>
          </div>
        </Modal>
      )}

      {toast && <div style={toastStyle}>{toast}</div>}
    </div>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      style={backdropStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={modalStyle} role="dialog" aria-modal="true">
        <div style={modalHeaderStyle}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close">x</button>
        </div>
        {children}
      </div>
    </div>
  )
}

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  background: '#fff',
  cursor: 'pointer',
}

const primaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const dangerBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#c62828',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '16px',
}

const noticeStyle: React.CSSProperties = {
  background: '#e3f2fd',
  border: '1px solid #90caf9',
  borderRadius: 4,
  padding: '10px 14px',
  fontSize: 12,
  color: '#1565c0',
  marginBottom: 16,
}

const errBannerStyle: React.CSSProperties = {
  color: '#c62828',
  background: '#fdecea',
  padding: '10px 14px',
  borderRadius: 4,
  marginBottom: 16,
  fontSize: 13,
}

const warningBoxStyle: React.CSSProperties = {
  background: '#fff8e1',
  border: '1px solid #ffd54f',
  borderRadius: 4,
  padding: '10px 14px',
  fontSize: 13,
  color: '#5d4037',
  marginBottom: 16,
  lineHeight: 1.5,
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: '20px 24px',
  width: 520,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
}

const modalHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: '1px solid #e0e0e0',
}

const closeBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 16,
  color: '#888',
  padding: '2px 6px',
  fontFamily: 'inherit',
}

const toastStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  background: '#1a1a2e',
  color: '#fff',
  padding: '10px 16px',
  borderRadius: 6,
  fontSize: 13,
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  zIndex: 2000,
}
